/**
 * Khung văn bản giấy theo Nghị định 30/2020/NĐ-CP: phần đầu (Quốc hiệu, cơ quan, số ký hiệu, địa danh,
 * ngày), khối tiêu đề, phần ký và nơi nhận. Chuyển nguyên từ `MauVanBanFormDialog.tsx` và `docFrame.ts`
 * của foxainative.
 *
 * Chữ ở đây là **nội dung văn bản hành chính**, không phải chuỗi giao diện: văn bản Việt Nam luôn ghi
 * "CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM" dù người dùng chọn ngôn ngữ nào, nên không đi qua catalog.
 * Đường kẻ dùng `currentColor` để ăn theo màu mực của `.mvb-doc`, không viết hex.
 */
import { thayBangNhan, thoatHtml, tuTheThucMau } from './mau-van-ban.ts';
import type { TheThucNhap } from './mau-van-ban.ts';
import type { TheThucMau, TruongMau } from '../types.ts';

const QUOC_HIEU = 'CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM';
const TIEU_NGU = 'Độc lập - Tự do - Hạnh phúc';
const KE = (rong: number, le: string) =>
  `<div style="width:${rong}px;border-bottom:1px solid currentColor;margin:${le};"></div>`;

const hai = (n: number) => String(n).padStart(2, '0');

/** "ngày 05 tháng 06 năm 2026". */
export function chuoiNgay(d: Date): string {
  return `ngày ${hai(d.getDate())} tháng ${hai(d.getMonth() + 1)} năm ${d.getFullYear()}`;
}

/** Cơ quan, chức vụ viết hoa; còn lại chỉ cắt khoảng trắng. */
function chuanHoa(f: TheThucNhap): TheThucNhap {
  const hoa = (s: string) => s.trim().toUpperCase();
  const gon = (s: string) => s.trim();
  return {
    cqch: hoa(f.cqch),
    cq: hoa(f.cq),
    so: gon(f.so),
    kyhieu: gon(f.kyhieu),
    dd: gon(f.dd),
    ky: gon(f.ky),
    cv: hoa(f.cv),
    nn: gon(f.nn),
    headerCenter: f.headerCenter,
  };
}

function dongSo(kyHieuMau: string, info: TheThucNhap): string {
  if (!info.so) return '';
  const kh = info.kyhieu ? `-${info.kyhieu}` : '';
  return `${info.so}/${kyHieuMau.trim() || '…'}${kh}`;
}

/** Khối tên loại văn bản căn giữa. */
export function khoiTieuDe(tieuDe: string): string {
  return (
    `<div style="text-align:center;margin:18px 0 12px;">` +
    `<div style="font-weight:bold;font-size:14.5px;">${thoatHtml(tieuDe)}</div>` +
    `${KE(90, '6px auto 0')}</div>`
  );
}

/** Phần đầu và tiêu đề; `homNay` truyền vào để test kiểm được ngày. */
export function phanDau(ten: string, kyHieuMau: string, theThuc: TheThucNhap, homNay = new Date()): string {
  const info = chuanHoa(theThuc);
  const tieuDe = (ten.trim() || '(Tên mẫu văn bản)').toUpperCase();
  const ngay = info.dd ? `${thoatHtml(info.dd)}, ${chuoiNgay(homNay)}` : chuoiNgay(homNay);
  // Đơn, tờ khai: Quốc hiệu căn giữa, ngày căn phải, không có cột cơ quan.
  if (info.headerCenter) {
    return (
      `<div style="text-align:center;">` +
      `<div style="font-weight:bold;">${QUOC_HIEU}</div>` +
      `<div style="font-weight:bold;">${TIEU_NGU}</div>` +
      `${KE(180, '3px auto 4px')}</div>` +
      `<div style="text-align:right;font-style:italic;margin-bottom:6px;">${ngay}</div>` +
      khoiTieuDe(tieuDe)
    );
  }
  const trai: string[] = [];
  if (info.cqch) trai.push(`<div>${thoatHtml(info.cqch)}</div>`);
  if (info.cq) trai.push(`<div style="font-weight:bold;">${thoatHtml(info.cq)}</div>`);
  if (info.cqch || info.cq) trai.push(KE(70, '3px auto 8px'));
  const so = dongSo(kyHieuMau, info);
  if (so) trai.push(`<div>Số: ${thoatHtml(so)}</div>`);
  return (
    `<table style="border-collapse:collapse;width:100%;font-size:13px;"><tr>` +
    `<td style="width:42%;text-align:center;vertical-align:top;padding:0;">${trai.join('')}</td>` +
    `<td style="text-align:center;vertical-align:top;padding:0;">` +
    `<div style="font-weight:bold;">${QUOC_HIEU}</div>` +
    `<div style="font-weight:bold;">${TIEU_NGU}</div>` +
    `${KE(170, '3px auto 8px')}` +
    `<div style="font-style:italic;">${ngay}</div>` +
    `</td></tr></table>` +
    khoiTieuDe(tieuDe)
  );
}

/** Khối "Nơi nhận" canh trái, mỗi dòng một nơi. */
function noiNhan(info: TheThucNhap): string {
  if (!info.nn) return '';
  const dong = info.nn
    .split(/\n/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => `<div style="text-indent:0;">- ${thoatHtml(l.replace(/^[-–•]\s*/, ''))}</div>`)
    .join('');
  return `<div style="font-size:12px;line-height:1.5;"><div style="font-weight:bold;font-style:italic;">Nơi nhận:</div>${dong}</div>`;
}

/** Phần ký (và nơi nhận, trừ đơn từ). */
export function phanKy(theThuc: TheThucNhap): string {
  const info = chuanHoa(theThuc);
  if (info.headerCenter) {
    return (
      `<div style="text-align:right;margin-top:26px;font-size:13px;">` +
      `<div style="font-weight:bold;">${thoatHtml(info.cv || 'NGƯỜI LÀM ĐƠN')}</div>` +
      `<div style="font-style:italic;font-size:12px;margin-top:2px;">(Ký, ghi rõ họ tên)</div>` +
      `<div style="height:46px;"></div>` +
      (info.ky ? `<div style="font-weight:bold;">${thoatHtml(info.ky)}</div>` : '') +
      `</div>`
    );
  }
  return (
    `<table style="width:100%;border-collapse:collapse;margin-top:26px;font-size:13px;"><tr>` +
    `<td style="width:50%;vertical-align:top;">${noiNhan(info)}</td>` +
    `<td style="text-align:center;vertical-align:top;">` +
    (info.cv ? `<div style="font-weight:bold;">${thoatHtml(info.cv)}</div>` : '') +
    (info.cv || info.ky ? `<div style="font-style:italic;font-size:12px;margin-top:2px;">(Đã ký)</div>` : '') +
    `<div style="height:46px;"></div>` +
    (info.ky ? `<div style="font-weight:bold;">${thoatHtml(info.ky)}</div>` : '') +
    `</td></tr></table>`
  );
}

/**
 * Bản xem trước của màn "Xem mẫu": thay `{{key}}` bằng `[Nhãn]`, ghép khung khi mẫu có cơ quan ban hành
 * hoặc chọn Quốc hiệu căn giữa (như `renderTemplatePreview` của bản cũ).
 */
export function xemTruocMau(
  than: string | null,
  fields: TruongMau[] | null,
  theThuc: TheThucMau | null,
  ten: string,
  kyHieuMau: string,
  homNay = new Date(),
): string {
  const ruot = thayBangNhan(than ?? '', fields);
  const info = tuTheThucMau(theThuc);
  if (info.cq.trim() || info.cqch.trim() || info.headerCenter) {
    return phanDau(ten, kyHieuMau, info, homNay) + ruot + phanKy(info);
  }
  return ruot;
}
